import { RecognitionBadge } from "@/components/recognition-badge/RecognitionBadge.tsx";
import { useRecognitions, useRecognitionsSort } from "@/queries/use-recognitions.query.ts";
import { ApiRecognition } from "@/types/recognitions/recognition.types.ts";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import {
  Button,
  FontAwesomeIcon,
  Modal,
  ModalProps,
  Skeleton,
  Stack,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export interface RecognitionSortModalProps extends ModalProps {
  categoryId: number;
}

export function RecognitionSortModal({ categoryId, ...props }: RecognitionSortModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = props;

  const { data: recognitions, isPending } = useRecognitions(categoryId);
  const recognitionsSort = useRecognitionsSort();

  const categoryRecognitions = useMemo(() => recognitions ?? [], [recognitions]);
  const syncKey = categoryRecognitions.map((item) => item.id).join(",");

  const [sorted, setSorted] = useState<ApiRecognition[]>(categoryRecognitions);
  const [syncedKey, setSyncedKey] = useState(syncKey);

  if (syncKey !== syncedKey) {
    setSyncedKey(syncKey);
    setSorted(categoryRecognitions);
  }

  const saveOrder = () => {
    recognitionsSort.mutate(
      { categoryId, ids: sorted.map((recognition) => recognition.id) },
      { onSuccess: () => close(id) }
    );
  };

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    if (!destination) return;

    const items = Array.from(sorted);
    const [moved] = items.splice(source.index, 1);
    items.splice(destination.index, 0, moved);
    setSorted(items);
  };

  return (
    <Modal
      title={t("common:reordering")}
      {...props}
      withCloser={true}
      actions={
        <Stack>
          <Button size={"lg"} onClick={saveOrder} isLoading={recognitionsSort.isPending}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      {isPending ? (
        <Stack direction={"vertical"} gap={"md"}>
          {Array.from({ length: 5 }).map((_, index) => (
            <Stack key={index} direction={"horizontal"} gap={"md"} items={"center"}>
              <Skeleton className={"w-9 h-9 rounded-sm"} />
              <Skeleton className={"h-4 w-48"} />
            </Stack>
          ))}
        </Stack>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId={"recognitions-list"} direction={"vertical"}>
            {(provided) => (
              <div
                className={"w-full flex flex-col overflow-y-auto"}
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {sorted.map((recognition, index) => (
                  <Draggable
                    key={recognition.id}
                    draggableId={recognition.id.toString()}
                    index={index}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={"flex items-center gap-4 bg-light py-2 min-h-16"}
                      >
                        <div
                          className={"flex items-center justify-center cursor-grab text-secondary"}
                        >
                          <FontAwesomeIcon icon={"fa-light fa-grip-dots-vertical"} />
                        </div>
                        <RecognitionBadge template={recognition} size={"xs"} />
                        <Title header={recognition.title} />
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </Modal>
  );
}
